export interface IdleManagerOptions {
  timeout?: number;
  onIdle?: () => void | Promise<void>;
  onActivity?: () => void | Promise<void>;
}

export default class IdleManager {
  private readonly timeout: number;
  private readonly onIdle?: () => void | Promise<void>;
  private readonly onActivity?: () => void | Promise<void>;

  private idleTimer: number | null = null;
  private lastActivity: number = Date.now();

  constructor({ timeout = 300000, onIdle, onActivity }: IdleManagerOptions) {
    this.timeout = timeout;
    this.onIdle = onIdle;
    this.onActivity = onActivity;

    this.handleActivity = this.handleActivity.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
  }

  public start(): void {
    window.addEventListener("mousemove", this.handleActivity);
    window.addEventListener("keydown", this.handleActivity);
    window.addEventListener("scroll", this.handleActivity);
    document.addEventListener("visibilitychange", this.handleVisibilityChange);

    this.resetTimer();
  }

  public stop(): void {
    window.removeEventListener("mousemove", this.handleActivity);
    window.removeEventListener("keydown", this.handleActivity);
    window.removeEventListener("scroll", this.handleActivity);
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);

    if (this.idleTimer) {
      window.clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private handleActivity(): void {
    this.lastActivity = Date.now();

    if (this.onActivity) {
      this.onActivity();
    }

    this.resetTimer();
  }

  private handleVisibilityChange(): void {
    if (!document.hidden) {
      this.handleActivity();
    }
  }

  private resetTimer(): void {
    if (this.idleTimer) {
      window.clearTimeout(this.idleTimer);
    }

    this.idleTimer = window.setTimeout(() => {
      const elapsed = Date.now() - this.lastActivity;

      if (elapsed >= this.timeout && this.onIdle) {
        this.onIdle();
        this.resetTimer();
      }
    }, this.timeout);
  }

  public __forceIdleForTests(): void {
    if (this.onIdle) this.onIdle();
  }
}
